// Clinician workload for one clinic-week, for the moment work is assigned
// (layer 3).
//
// The whole cohort arrives in one request rather than one per candidate: the
// number only means anything comparatively, and a scheduler weighing four
// people should not see three of them while the fourth is still loading.

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  schedulingPointKeys,
  SCHEDULING_POINT_LOAD_STALE_TIME_MS,
} from '../services/queryClient';
import { getSchedulingPointLoad } from '../services/schedulingPointsApi';
import { useCanViewSchedulingPointLoads } from './useCanViewSchedulingPointLoads';
import { getISOWeekStartISO } from '../utils/dateUtils';
import type {
  ClinicianLoad,
  SchedulingPointLoadWeek,
} from '../services/api/types.gen';

export interface UseSchedulingPointLoadsArgs {
  /** The clinic whose values price the week. */
  clinicId: string | null | undefined;
  /** Any date in the week of interest, "YYYY-MM-DD". */
  date: string | null | undefined;
  /**
   * Caller's own gate — a closed dialog, a patient with no clinic. ANDed with
   * the permission check rather than replacing it.
   */
  enabled?: boolean;
}

export interface UseSchedulingPointLoadsResult {
  /** The week, or undefined when disabled, loading, or failed. */
  week: SchedulingPointLoadWeek | undefined;
  /** Keyed by `user_id` — the person, not their `UserClinic` posting. */
  loadsByUserId: Map<string, ClinicianLoad>;
  isLoading: boolean;
}

const EMPTY_LOADS: Map<string, ClinicianLoad> = new Map();

export function useSchedulingPointLoads({
  clinicId,
  date,
  enabled = true,
}: UseSchedulingPointLoadsArgs): UseSchedulingPointLoadsResult {
  const canView = useCanViewSchedulingPointLoads();
  // Only to prove the date parses; the request carries the date itself.
  const weekStart = getISOWeekStartISO(date);

  const isEnabled = enabled && canView && !!clinicId && !!weekStart;

  const { data, isPending } = useQuery({
    // Keyed on the *task's own date*, not on its week.
    //
    // Sharing one entry per week looked like a free saving and was not: the
    // response is week-wide except for `target_date` and the `target_day_*`
    // tallies, which describe the day being scheduled. Sending the week's
    // Monday made every "this task's day" figure describe Monday — and where
    // Monday happened to be empty, the day line vanished entirely, so a
    // clinician with an impossible Wednesday read as free on the very day the
    // scheduler was booking. Two tasks on the same day still share one fetch.
    queryKey: schedulingPointKeys.load(clinicId ?? '', date ?? ''),
    queryFn: async () => {
      const response = await getSchedulingPointLoad({
        query: { clinic_id: clinicId!, date: date! },
      });
      // The generated client resolves HTTP failures as `{ error }` rather than
      // rejecting (throwOnError is false), so this has to be checked by hand —
      // without it a 500 caches as a successful *empty cohort*, which renders
      // as every clinician carrying nothing. That is the one wrong answer this
      // feature must never give.
      if (response.error) throw response.error;
      return response.data;
    },
    enabled: isEnabled,
    staleTime: SCHEDULING_POINT_LOAD_STALE_TIME_MS,
    // No `meta.errorToast`, deliberately. This is an annotation on a picker
    // that worked fine before it existed; a broken endpoint must leave the
    // picker exactly as it was and say nothing. The shared handler still logs.
    //
    // And no retry: a second request buys a decoration, and a scheduler opening
    // dropdowns against a failing endpoint should not double the traffic.
    retry: false,
  });

  const loadsByUserId = useMemo(() => {
    if (!data?.loads) return EMPTY_LOADS;
    return new Map(data.loads.map(load => [load.user_id, load]));
  }, [data]);

  return {
    week: data,
    loadsByUserId,
    // `isPending` is Query's "no data yet" — but a *disabled* query is also
    // pending forever, and that is not loading, it is off. Callers use this to
    // decide whether to wait, so conflating the two would hang a spinner on a
    // picker that is never going to fetch.
    isLoading: isEnabled && isPending,
  };
}

/**
 * Invalidate every cached workload week.
 *
 * Called after an assignment lands. Without it a scheduler assigns three visits
 * and watches the numbers sit still for a minute — which does not read as
 * staleness, it reads as the numbers being decorative, and a number nobody
 * trusts is worse than no number.
 *
 * Invalidates all clinics and weeks rather than the one just touched: moving a
 * task changes the losing clinician's week too, and a cross-clinic candidate's
 * week is counted at more than one clinic's values.
 */
export function useInvalidateSchedulingPointLoads(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useMemo(
    () => async () => {
      await queryClient.invalidateQueries({
        queryKey: schedulingPointKeys.loads(),
      });
    },
    [queryClient]
  );
}
